/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';

// Types
import { ApplicationState } from 'store';
import { CompanyReportInterface } from 'store/ducks/companies/types';

// Styles
import useStyle from './transactionNavigation.styles';

interface TransactionNavigationProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  groupings: Array<string>;
  transactionslist?: CompanyReportInterface[];
  groupedColumns?: () => {
    key: string;
    name: string;
  }[];
  fieldFormatting?: Map<string, (arg: any) => any>;
}

type Props = TransactionNavigationProps;

const TransactionNavigation: React.FC<Props> = ({
  setTableReference,
  setTableCSV,
  groupings,
  transactionslist,
  groupedColumns,
  fieldFormatting,
}) => {
  const classes = useStyle();

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const requestsList = useSelector((state: ApplicationState) => {
    const list = transactionslist || state.companies.companyReportList;

    if (list.length === 0) return [];

    if (groupings.length === 0) {
      return list.map((r) => ({
        createdAt: r.createdAt,
        paymentMethod: r.paymentMethod,
        status: r.status,
        id: r.id,
        name: r.name,
        document: r.document ? r.document : r.cnpj,
        value: r.value,
        orderValue: r.value,
        nsu: r.nsu,
        tax: r?.Taxes?.reduce((acc, curr) => acc + curr.value, 0),
      }));
    }

    // Handle grouped data
    return list
      .map((item: any) => {
        const baseData = {
          count: item.count,
          totalValue: item.totalValue,
        };

        const groupedData = groupings.reduce((acc, grouping) => {
          const fieldName = grouping;
          const fieldValue = item[fieldName as keyof any];

          if (fieldValue !== undefined) {
            return {
              ...acc,
              [fieldName]: fieldValue,
            };
          }
          return acc;
        }, baseData);

        return groupedData;
      })
      ?.sort((a: any, b: any) => {
        if (a?.usedDate === undefined && b?.usedDate === undefined) {
          return 0;
        }

        if (a?.usedDate === undefined) {
          return 1;
        }

        if (b?.usedDate === undefined) {
          return -1;
        }

        const dateA = a?.usedDate
          .split('/')
          .reverse()
          .join('');

        const dateB = b?.usedDate
          .split('/')
          .reverse()
          .join('');

        return dateA.localeCompare(dateB);
      });
  });

  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState<Order>('asc');
  const [tableColumns, setTableColumns] = useState<
    Array<{ key: string; name: string }>
  >([]);

  useEffect(() => {
    if (groupedColumns) setTableColumns(groupedColumns());
  }, [groupings]);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (tableColumns.length > 0 && requestsList.length > 0) {
      csv = tableColumns.reduce((a, c) => `${a}${a ? ';' : ''}${c.name}`, '');
      if (transportOperatorSelected.type === 1) csv += ';Operação';

      let totalValueSum = 0;
      let totalOrderValueSum = 0;

      stableSort(requestsList, getComparator(order, orderBy)).forEach(
        (t: { [x: string]: any }) => {
          csv += '\n';
          tableColumns.forEach((c) => {
            const formatter = fieldFormatting && fieldFormatting.get(c.key);
            let finalOrderValue = 0;
            if (c.key === 'orderValue') {
              const taxReduction =
                t?.Taxes?.reduce(
                  (acc: any, curr: any) => acc + curr.value,
                  0,
                ) || 0;
              finalOrderValue = taxReduction ? t.value - taxReduction : t.value;
              totalOrderValueSum += finalOrderValue || 0;
            }
            if (c.key === 'value') {
              totalValueSum += t[c.key] || 0;
            }
            if (c.key === 'totalValue') {
              totalValueSum += t[c.key] || 0;
            }

            csv += finalOrderValue
              ? `${finalOrderValue?.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })};`
              : ` ${formatter ? formatter(t[c.key]) : t[c.key] || '--'};`;
          });
          if (transportOperatorSelected.type === 1) {
            csv +=
              transportOperatorsList.find(
                (e) => e.id === t?.transportOperatorId,
              )?.name || '---';
          }
        },
      );

      // Summary row
      csv += '\n';
      tableColumns.forEach((c, idx) => {
        if (idx === 0) {
          csv += 'Total;';
        } else if (c.key === 'value' || c.key === 'totalValue') {
          csv += `${totalValueSum?.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })};`;
        } else if (c.key === 'orderValue') {
          csv += `${totalOrderValueSum?.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })};`;
        } else {
          csv += ';';
        }
      });
      if (transportOperatorSelected.type === 1) {
        csv += '';
      }
    }
    setTableCSV(csv);
    // eslint-disable-next-line
  }, [tableColumns, requestsList]);

  return (
    <TableContainer
      className={classes.tableContainer}
      ref={(ref: any) => setTableReference(ref)}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {tableColumns.length > 0 &&
              tableColumns.map((col) => (
                <TableCell
                  key={col.key}
                  variant='head'
                  align='center'
                  sortDirection={orderBy === col.key ? order : false}>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, col.key)}>
                    {col.name}
                    {orderBy === col.key ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {requestsList.length > 0 &&
            stableSort(requestsList, getComparator(order, orderBy)).map(
              (request) => {
                let finalOrderValue = request?.value ?? 0;
                return (
                  <TableRow style={{ padding: 16 }} key={request.id}>
                    {tableColumns.map((col) => {
                      const taxValue = request?.tax;
                      const formatter =
                        fieldFormatting && fieldFormatting.get(col.key);

                      if (col.key === 'status' && formatter) {
                        const status = formatter(request[col.key]);

                        return (
                          <TableCell
                            style={{
                              color:
                                status === 'Completado'
                                  ? '#22D76A'
                                  : status === 'Erro'
                                  ? '#E22020'
                                  : status === 'Em Processamento'
                                  ? '#AAB8C2'
                                  : '',
                            }}
                            key={`${request.id}-${col.key}`}
                            align='center'>
                            {status || '--'}
                          </TableCell>
                        );
                      }

                      if (col.key === 'tax' && formatter) {
                        const decimalTaxValue = (taxValue ?? 0) / 100;
                        const taxAmount =
                          (request?.value ?? 0) * decimalTaxValue;
                        finalOrderValue -= taxAmount;

                        return (
                          <TableCell
                            key={`${request.id}-${col.key}`}
                            align='center'>
                            {taxValue
                              ? ` R$ ${taxAmount?.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                })} `
                              : '---'}
                          </TableCell>
                        );
                      }

                      if (col.key === 'orderValue') {
                        return (
                          <TableCell
                            key={`${request.id}-${col.key}`}
                            align='center'>
                            {taxValue > 0
                              ? ` R$ ${finalOrderValue?.toLocaleString(
                                  'pt-BR',
                                  {
                                    minimumFractionDigits: 2,
                                  },
                                )} `
                              : request[col.key]?.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                })}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={`${request.id}-${col.key}`}
                          align='center'>
                          {formatter
                            ? formatter(request[col.key])
                            : request[col.key] || '--'}
                        </TableCell>
                      );
                    })}
                    {transportOperatorSelected.type === 1 && (
                      <TableCell>
                        {transportOperatorsList.find(
                          (e) => e.id === request?.transportOperatorId,
                        )?.name || '---'}
                      </TableCell>
                    )}
                  </TableRow>
                );
              },
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { TransactionNavigation };
export default TransactionNavigation;
